import type { ReceiptItemDTO } from "@/modules/billing/presentation/api/client";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import {
  formatAssistInstructorBillHtml,
  formatBillAmount,
} from "@/shared/lib/receipt-print-48mm";
import {
  groupRoomBookingReceiptItems,
  hasPricedAssistReceiptItems,
} from "../ui/RoomBookingReceiptGroupCard";

function buildOtherReceiptItemHtml(item: ReceiptItemDTO): string {
  const descriptionLines = item.description
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  let lines = descriptionLines
    .map((line) => `<div class="bill-item-line">${line}</div>`)
    .join("");

  const hasInlineSchedule = item.description.includes("\n");
  if (!hasInlineSchedule && item.roomName) {
    lines += `<div class="bill-item-line">ຫ້ອງ: ${item.roomName}</div>`;
  }
  if (item.startDate && item.expireDate) {
    lines += `<div class="bill-item-line">ໄລຍະຮຽນ: ${formatDate(item.startDate)} - ${formatDate(item.expireDate)}</div>`;
  }
  if (item.selectedClasses && item.selectedClasses.length > 0) {
    for (const c of item.selectedClasses) {
      lines += `<div class="bill-item-line">${c.classTypeName} - ${c.levelName}</div>`;
    }
  }
  if (!hasInlineSchedule && item.date) {
    lines += `<div class="bill-item-line">ວັນທີ: ${formatDate(item.date)}</div>`;
    if (item.startTime && item.endTime) {
      lines += `<div class="bill-item-line">ເວລາ: ${formatTime(item.startTime)} - ${formatTime(item.endTime)}</div>`;
    }
  }
  lines += formatAssistInstructorBillHtml(item.assistInstructorNames);
  lines += `<div class="bill-item-line">${item.quantity} x ${formatBillAmount(item.unitPrice)} = ${formatBillAmount(item.totalPrice)} ກີບ</div>`;
  return lines;
}

function buildRoomBookingGroupHtml(
  group: ReturnType<typeof groupRoomBookingReceiptItems>["roomBookingGroups"][number],
  showLegacyAssistNames: boolean,
): string {
  const { roomItem, assistItems } = group;
  const descriptionLines = roomItem.description
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  let html = descriptionLines
    .map((line) => `<div class="bill-item-line">${line}</div>`)
    .join("");

  const hasInlineSchedule = roomItem.description.includes("\n");
  if (!hasInlineSchedule && roomItem.roomName) {
    html += `<div class="bill-item-line">ຫ້ອງ: ${roomItem.roomName}</div>`;
  }
  if (!hasInlineSchedule && roomItem.date) {
    html += `<div class="bill-item-line">ວັນທີ: ${formatDate(roomItem.date)}</div>`;
    if (roomItem.startTime && roomItem.endTime) {
      html += `<div class="bill-item-line">ເວລາ: ${formatTime(roomItem.startTime)} - ${formatTime(roomItem.endTime)}</div>`;
    }
  }

  html += `<div class="bill-item-line bill-item-strong">ຈອງຫ້ອງ</div>`;
  html += `<div class="bill-item-line">${roomItem.quantity} x ${formatBillAmount(roomItem.unitPrice)} = ${formatBillAmount(roomItem.totalPrice)} ກີບ</div>`;

  for (const assist of assistItems) {
    html += `<div class="bill-item-line bill-item-strong">${assist.description}</div>`;
    html += `<div class="bill-item-line">${assist.quantity} x ${formatBillAmount(assist.unitPrice)} = ${formatBillAmount(assist.totalPrice)} ກີບ</div>`;
  }

  if (showLegacyAssistNames && assistItems.length === 0) {
    html += formatAssistInstructorBillHtml(roomItem.assistInstructorNames);
  }

  html += `<hr class="bill-sep bill-sep-light" />`;
  return html;
}

export function buildGroupedReceiptItemsHtml(items: ReceiptItemDTO[]): string {
  const { roomBookingGroups, orphanAssistItems, otherItems } =
    groupRoomBookingReceiptItems(items);
  const showLegacyAssist = !hasPricedAssistReceiptItems(items);

  let html = roomBookingGroups
    .map((group) => buildRoomBookingGroupHtml(group, showLegacyAssist))
    .join("");

  for (const assist of orphanAssistItems) {
    html += `<div class="bill-item-line bill-item-strong">${assist.description}</div>`;
    html += `<div class="bill-item-line">${assist.quantity} x ${formatBillAmount(assist.unitPrice)} = ${formatBillAmount(assist.totalPrice)} ກີບ</div>`;
    html += `<hr class="bill-sep bill-sep-light" />`;
  }

  html += otherItems.map((item) => buildOtherReceiptItemHtml(item)).join("");
  return html;
}
